/**
 * The order session (orderId + guest_session_token) also lives in the URL.
 *
 * That matters when a session has to be dropped: the restore in
 * contexts/index.tsx reads the URL BEFORE the cookie, so clearing cookies +
 * React state while leaving the pair in the query string means the very next
 * reload re-adopts the dead order and the clear achieved nothing. Every drop
 * has to reach all three stores.
 */

import { forgetGuestTokenHandoff } from "./guest-token-url-guard";

/**
 * Which pathnames are order-VIEW routes: /sl/order/<id>/summary, /delivery,
 * /photo-options, /order/history, ...
 *
 * These pages exist to display ONE specific order — a finalized one, most of
 * the time, since /summary is where the customer lands after paying. They take
 * the order id from the route and the guest token from the QUERY STRING (see
 * OrderInitializer), so cart-restore logic must not clear either out from under
 * them: stripping guest_session_token there would 401 every guest's receipt
 * page, and nulling the active order id is what previously fought
 * OrderInitializer in a set→clear→set loop that hammered the API until it
 * rate-limited (429) and the page flickered.
 *
 * Matches with or without a locale prefix (/order/..., /sl/order/..., and the
 * de-AT style two-part locales).
 */
export function isOrderViewPath(pathname: string): boolean {
  return /(^|\/)order\/[^/]+(\/|$)/.test(pathname);
}

/**
 * Every query param that can carry the order session.
 *
 * `guestToken` is the LEGACY spelling. Both readers accept it as a fallback
 * (`getUrlParameter("guest_session_token") || getUrlParameter("guestToken")`),
 * so a clear that removes only the canonical name leaves a live half-session in
 * the address bar and the next load re-adopts it. Deleting one spelling but not
 * the other is precisely the shape of the home-page bug this list exists to
 * prevent: it dropped `orderId` + `guestToken` and left `guest_session_token`
 * sitting in the URL.
 */
const ORDER_SESSION_PARAMS = ["orderId", "guest_session_token", "guestToken"];

/**
 * Remove the order session (orderId + guest token, either spelling) from the
 * address bar, leaving every other query param (utm_*, fbclid, ...) untouched.
 *
 * Uses history.replaceState with the CURRENT history.state preserved, exactly
 * the way ensureOrderExists puts the pair there — passing null state can break
 * App Router client-side navigation.
 *
 * The token itself is usually no longer in the address bar at all: the head
 * guard (guest-token-url-guard.ts) moves it into a per-order hand-off so that
 * analytics tags cannot read it, and the shop's readers fall back to that
 * hand-off for the order the URL names. So the hand-off is the fourth store a
 * drop has to reach — leave it, and the next load re-adopts the session from
 * `?orderId=` plus the token kept for it.
 *
 * @returns true if the URL was rewritten or a kept token dropped, false if
 *          there was nothing to remove (so callers/tests can tell a no-op from
 *          a real clear).
 */
export function clearOrderSessionFromUrl(): boolean {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  const forgot = forgetGuestTokenHandoff(params.get("orderId"));
  if (!ORDER_SESSION_PARAMS.some((p) => params.has(p))) return forgot;
  for (const p of ORDER_SESSION_PARAMS) params.delete(p);
  const query = params.toString();
  window.history.replaceState(
    window.history.state,
    "",
    window.location.pathname + (query ? `?${query}` : "")
  );
  return true;
}
